import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { styled as styled2, css } from '@compiled/react';
import { format, parseISO } from 'date-fns';
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import DynamicTable from '@atlaskit/dynamic-table';
import type { SortOrderType } from '@atlaskit/dynamic-table/types';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	pullRequestTable_softwareCodePage$key,
	DevOpsPullRequestStatus,
} from '@atlassian/jira-relay/src/__generated__/pullRequestTable_softwareCodePage.graphql';
import {
	COMMENTS_CELL_TEST_ID,
	LAST_UPDATED_CELL_TEST_ID,
	REVIEWERS_CELL_TEST_ID,
	STATUS_CELL_TEST_ID,
	SUMMARY_CELL_TEST_ID,
	PULL_REQUESTS_TABLE_PAGE_SIZE,
	PULL_REQUESTS_DYNAMIC_TABLE_TEST_ID,
} from '../../../../../../../common/constants';
import { useIsOverflowWidth } from '../../../../../../../common/utils';
import { usePullRequestTableOptionsStore } from '../../../../../../../controllers/pull-request-filters';
import CommentsCell from './comments-cell';
import LastUpdatedCell from './last-updated-cell';
import messages from './messages';
import NoResults from './no-results';
import ReviewersCell, { NoReviewersText, ReviewersCellMessages } from './reviewers-cell';
import StatusCell from './status-cell';
import SummaryCell from './summary-cell';

// convert to the sortable key supporting primary/secondary key with unique id.
export const convertToSortableKey = (
	primarySortKey: string | number,
	secondarySortKey: string,
	id: string,
): string => `${primarySortKey}_${secondarySortKey}_${id}`;

// convert to key value defines sort order for status column - OPEN < MERGED < DECLINED < UNKNOWN
export const transformStatusKeyValue = (
	status: DevOpsPullRequestStatus | null | undefined,
): string => {
	switch (status) {
		case 'OPEN':
			return '0';
		case 'MERGED':
			return '1';
		case 'DECLINED':
			return '2';
		case 'UNKNOWN':
			return '3';
		default:
			return '9';
	}
};

export const transformDateKeyValue = (dateInIsoFormat: string | null | undefined): string => {
	let convertedDate = '';
	if (dateInIsoFormat) {
		convertedDate = format(parseISO(dateInIsoFormat), 'T');
	} else {
		// default date in odd-case no date is returned from backend.
		convertedDate = format(Date.now(), 'T');
	}
	return convertedDate;
};

export const transformCommentCountKeyValue = (commentCount: number | null | undefined): number => {
	if (commentCount) {
		return commentCount;
	}
	return 0;
};

const asSortOrderType = (sortOrder: string): SortOrderType =>
	sortOrder === 'ASC' ? 'ASC' : 'DESC';

const MemoisedTable = memo(DynamicTable);
const PullRequestTable = ({
	pullRequestTableData,
	isLoading,
}: {
	pullRequestTableData: pullRequestTable_softwareCodePage$key;
	isLoading: boolean;
}) => {
	const { formatMessage } = useIntl();
	const [page, setPage] = useState(1);
	const [pullRequestTableOptions, actions] = usePullRequestTableOptionsStore();
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const isTableOverflowing = useIsOverflowWidth(scrollContainerRef);

	const onSort = (data: { key: string; sortOrder: string }, analyticsEvent?: UIAnalyticsEvent) => {
		// @ts-expect-error - Argument of type 'UIAnalyticsEvent | undefined' is not assignable to parameter of type 'UIAnalyticsEvent'.
		fireUIAnalytics(analyticsEvent, 'pullRequestListSort', {
			sortKey: data.key,
			sortDirection: data.sortOrder,
		});
		actions.setSortInfo(data.key, asSortOrderType(data.sortOrder));
	};

	const data = useFragment(
		graphql`
			fragment pullRequestTable_softwareCodePage on DevOpsPullRequestDetails @relay(plural: true) {
				id @required(action: THROW)
				status
				lastUpdated
				reviewers {
					__typename
				}
				commentCount
				...summaryCell_softwareCodePage
				...statusCell_softwareCodePage
				...reviewersCell_softwareCodePage
				...commentsCell_softwareCodePage
				...lastUpdatedCell_softwareCodePage
			}
		`,
		pullRequestTableData,
	);

	useEffect(() => {
		// Reset the page to 1 when the tables change via filters or sorts.
		setPage(1);
	}, [data]);
	const prHead = useMemo(
		() => ({
			cells: [
				{
					key: 'summary',
					content: formatMessage(messages.headerSummary),
				},
				{
					key: 'status',
					content: formatMessage(messages.headerStatus),
					shouldTruncate: true,
					isSortable: true,
					width: 12,
					testId: 'status',
				},
				{
					key: 'reviewers',
					content: formatMessage(messages.headerReviewers),
					shouldTruncate: true,
					isSortable: true,
					width: 12,
					testId: 'reviewers',
				},
				{
					key: 'comments',
					content: formatMessage(messages.headerComments),
					shouldTruncate: true,
					isSortable: true,
					width: 12,
					testId: 'comments',
				},

				{
					key: 'updated',
					content: formatMessage(messages.headerUpdated),
					shouldTruncate: true,
					isSortable: true,
					width: 12,
					testId: 'updated',
				},
			],
		}),
		[formatMessage],
	);
	const prRows = useMemo(
		() =>
			data.map((pr) => ({
				key: pr.id,
				cells: [
					{
						key: pr.id,
						content: (
							<TableCell data-testid={SUMMARY_CELL_TEST_ID}>
								<JSErrorBoundary
									teamName="fusion-isotopes"
									packageName="jiraSoftwareCodePage"
									id="jiraSoftwareCodePagePullRequestsSummaryCell"
									fallback={() => <ErrorText>{formatMessage(messages.summaryCellError)}</ErrorText>}
									sendToPrivacyUnsafeSplunk
								>
									<SummaryCell summaryCellData={pr} />
								</JSErrorBoundary>
							</TableCell>
						),
					},
					{
						key: convertToSortableKey(
							transformStatusKeyValue(pr.status),
							transformDateKeyValue(pr.lastUpdated),
							`${pr.id}-status`,
						),
						content: (
							<TableCell data-testid={STATUS_CELL_TEST_ID}>
								<StatusCell statusCellData={pr} />
							</TableCell>
						),
					},
					{
						key: convertToSortableKey(
							pr.reviewers?.length ?? 0,
							transformDateKeyValue(pr.lastUpdated),
							`${pr.id}-reviewers`,
						),
						content: (
							<TableCell
								data-reviewerCount={pr.reviewers?.length ?? 0}
								data-testid={REVIEWERS_CELL_TEST_ID}
							>
								<JSErrorBoundary
									teamName="fusion-isotopes"
									packageName="jiraSoftwareCodePage"
									id="jiraSoftwareCodePagePullRequestsReviewersCell"
									fallback={() => (
										<NoReviewersText>
											{formatMessage(ReviewersCellMessages.noReviewersMessage)}
										</NoReviewersText>
									)}
									sendToPrivacyUnsafeSplunk
								>
									<ReviewersCell reviewersCellData={pr} />
								</JSErrorBoundary>
							</TableCell>
						),
					},
					{
						key: convertToSortableKey(
							transformCommentCountKeyValue(pr.commentCount),
							transformDateKeyValue(pr.lastUpdated),
							`${pr.id}-comments`,
						),
						content: (
							<TableCell data-testid={COMMENTS_CELL_TEST_ID}>
								<CommentsCell commentsCellData={pr} />
							</TableCell>
						),
					},
					{
						key: convertToSortableKey(
							transformDateKeyValue(pr.lastUpdated),
							'',
							`${pr.id}-lastUpdated`,
						),
						content: (
							<TableCell data-testid={LAST_UPDATED_CELL_TEST_ID}>
								<JSErrorBoundary
									teamName="fusion-isotopes"
									packageName="jiraSoftwareCodePage"
									id="jiraSoftwareCodePagePullRequestsLastUpdatedCell"
									fallback={() => (
										<ErrorText>{formatMessage(messages.lastUpdatedCellError)}</ErrorText>
									)}
									sendToPrivacyUnsafeSplunk
								>
									<LastUpdatedCell lastUpdatedCellData={pr} />
								</JSErrorBoundary>
							</TableCell>
						),
					},
				],
			})),
		[data, formatMessage],
	);
	return (
		<TableWrapper>
			<TableOverflowWrapper isTableOverflowing={isTableOverflowing} ref={scrollContainerRef}>
				<MemoisedTable
					head={prHead}
					rows={prRows}
					rowsPerPage={isLoading ? undefined : PULL_REQUESTS_TABLE_PAGE_SIZE}
					defaultPage={1}
					onSetPage={setPage}
					onSort={onSort}
					sortKey={pullRequestTableOptions.sortKey ? pullRequestTableOptions.sortKey : undefined}
					sortOrder={asSortOrderType(pullRequestTableOptions.sortOrder)}
					testId={PULL_REQUESTS_DYNAMIC_TABLE_TEST_ID}
					defaultSortKey="updated"
					defaultSortOrder="DESC"
					page={page}
					emptyView={<NoResults />}
				/>
			</TableOverflowWrapper>
		</TableWrapper>
	);
};

export default PullRequestTable;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableCell = styled2.div({
	padding: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorText = styled2.span({
	fontStyle: 'italic',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N200),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableWrapper = styled2.div({
	width: '100%',
	position: 'relative',
});

/**
 * Extends dynamic-table styles to support horizontal scrolling for overflow
 * https://atlassian.design/components/dynamic-table/examples#overflow
 */
const TableOverflowWrapperAfterElementCssStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:after': {
		width: `${token('space.100', '8px')}px`,
		height: '100%',
		position: 'absolute',
		top: 0,
		right: 0,
		background: `linear-gradient(to right, ${token(
			'color.blanket',
			'rgba(99, 114, 130, 0)',
		)} 0px, ${token('color.blanket', 'rgba(9, 30, 66, 0.13)')} 100%)`,
		content: "''",
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableOverflowWrapper = styled2.div<{ isTableOverflowing: boolean }>(
	{
		overflowX: 'auto',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isTableOverflowing }) =>
		isTableOverflowing ? TableOverflowWrapperAfterElementCssStyles : undefined,
);
